import { Fragment, useRef } from 'react';
import { SECTION_IDS, site } from '../content/site';
import { gsap, useGSAP, EASE, MOTION_OK } from '../lib/gsap';
import { Horizons } from '../components/vision/Horizons';
import { Log } from '../components/vision/Log';
import { revealLines } from '../components/vision/lineReveal';
import { useDuskChapter } from '../components/vision/useDuskChapter';
import { HALO } from '../components/vision/halo';
import { glue, noWidow } from '../components/vision/glue';

/**
 * Vision: the chapter where the page turns to golden-hour dusk (the only theme switch).
 * The section paints no background: the body color (var(--page-bg)) changes, so the
 * fixed fiber canvas, now a meadow, shows through. Content lives in the upper and middle
 * of the viewport; the generous bottom space leaves the meadow calm before Contact.
 */
export function Vision() {
	const ref = useRef<HTMLElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const { eyebrow, titleLines, body, horizons, log } = site.vision;

	useDuskChapter(ref);

	useGSAP(
		() => {
			const section = ref.current;
			const title = titleRef.current;
			if (!section || !title) return;
			const mm = gsap.matchMedia();

			mm.add(MOTION_OK, (context) => {
				const cancelTitle = revealLines(title, context, { start: 'top 68%', delay: 0.1 });

				const eyebrowEl = section.querySelector('[data-vision-eyebrow]');
				const copy = section.querySelector('[data-vision-body]');

				if (eyebrowEl) {
					gsap.from(eyebrowEl, {
						y: 16,
						opacity: 0,
						duration: 1,
						ease: EASE,
						scrollTrigger: { trigger: title, start: 'top 68%' },
					});
				}
				if (copy) {
					gsap.from(copy, {
						y: 28,
						opacity: 0,
						duration: 1.1,
						ease: EASE,
						scrollTrigger: { trigger: copy, start: 'top 85%' },
					});
				}

				return cancelTitle;
			});
		},
		{ scope: ref },
	);

	return (
		<section
			id={SECTION_IDS.vision}
			ref={ref}
			aria-labelledby="vision-title"
			className="relative min-h-[100dvh] pb-[clamp(14rem,60vh,36rem)] pt-[clamp(6rem,10vw,9rem)] text-mineral"
		>
			<div className="container-site">
				<p data-vision-eyebrow className="text-[13px] font-medium uppercase tracking-[0.16em] text-solar">
					{eyebrow}
				</p>

				<h2
					id="vision-title"
					ref={titleRef}
					className="mt-6 text-balance font-display text-[clamp(2.5rem,5.6vw,6rem)] font-bold leading-[0.98] tracking-[-0.035em] text-mineral"
				>
					{/* Flat text + <br> children so SplitText keeps the authored line break. */}
					{titleLines.map((line, i) => (
						<Fragment key={line}>
							{i > 0 && (
								<>
									{' '}
									<br />
								</>
							)}
							{noWidow(line)}
						</Fragment>
					))}
				</h2>

				<p
					data-vision-body
					className={`mt-8 max-w-prose text-[clamp(1.0625rem,1.35vw,1.25rem)] leading-[1.6] text-dusk-muted md:mt-10 ${HALO}`}
				>
					{glue(body)}
				</p>

				{/* Each group gets close to half a viewport of air: while one is read in the upper
				    half, the meadow below stays clear. */}
				<div className="mt-[clamp(10rem,46vh,30rem)]">
					<Horizons items={horizons} firstExtra={<Log log={log} />} />
				</div>
			</div>
		</section>
	);
}
